/**
 * Командный раздел «Клиенты» (team-portal): реальный список клиентов (GET /api/team/clients,
 * закрывает заглушку Блока 1) + создание клиента-компании. Строки кликабельны — ведут в карточку
 * клиента (/team/clients/{cid}), где команда правит реквизиты/контакты/структуру.
 */
import { useEffect, useState, type FormEvent } from 'react'
import { Building2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import {
  teamApi,
  type ClientSummary,
  type CreateClientInput,
  type CreatedClient,
  type DaDataPartyMatch,
} from '@/lib/api'
import { getErrorMessage, fieldError } from '@/lib/errors'
import { cn } from '@/lib/utils'
import {
  isLegalEntityInn,
  isValidInn,
  validateRequisites,
  type RequisiteErrors,
} from '@/lib/validation'
import { ClientSearch } from '@/company/client-search'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Panel } from '@/design-system/components/layout/panel'
import { Toolbar } from '@/design-system/components/layout/toolbar'
import { Card } from '@/design-system/components/layout/card'
import { Grid } from '@/design-system/components/layout/grid'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { PasswordInput } from '@/design-system/components/primitives/password-input'
import { Alert } from '@/design-system/components/feedback/alert'
import { Hint } from '@/design-system/components/primitives/text'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'
import { Pagination } from '@/design-system/components/data/pagination'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'
import { canAssign, useSeesAllClients } from '@/team/roles'
import { useAuth } from '@/app/auth-context'
import { staffOptions, useStaff } from '@/team/use-staff'

const EMPTY_FORM: CreateClientInput = {
  name: '',
  inn: '',
  kpp: '',
  ogrn: '',
  okpo: '',
  oktmo: '',
  okved: '',
  legalAddress: '',
  email: '',
  phone: '',
  directorName: '',
  directorPosition: '',
  loginEmail: '',
  password: '',
  existingLoginEmail: '',
}

/**
 * Как клиент будет входить в новую организацию (create-client-with-existing-login): `new` —
 * отдельная почта и пароль организации; `existing` — вход специалиста, который уже работает в
 * кабинете с другой организацией: новая организация закрепляется за ним, почта и пароль не нужны.
 */
type LoginMode = 'new' | 'existing'

/** Выделение выбранного варианта — как у сегментированного выбора «Да»/«Нет» (YesNoGroup). */
const SELECTED_MODE = 'border-accent bg-accent-bg text-accent-txt hover:bg-accent-bg'

/**
 * Применяет найденную DaData организацию к форме создания клиента (design D5/D11):
 * заполняются ТОЛЬКО присутствующие в ответе поля — уже введённое пользователем не
 * затирается. `CreateClientInput` не содержит `nameShort`/`okogu` (форма их не
 * показывает вовсе — существующий пробел вне области p2-dadata), поэтому эти поля
 * DaData здесь не применяются.
 */
function applyDaDataMatchToClientForm(form: CreateClientInput, match: DaDataPartyMatch): CreateClientInput {
  const next = { ...form }
  if (match.nameFull) next.name = match.nameFull
  if (match.inn) next.inn = match.inn
  if (match.kpp) next.kpp = match.kpp
  if (match.ogrn) next.ogrn = match.ogrn
  if (match.okpo) next.okpo = match.okpo
  if (match.oktmo) next.oktmo = match.oktmo
  if (match.okved) next.okved = match.okved
  if (match.legalAddress) next.legalAddress = match.legalAddress
  if (match.directorName) next.directorName = match.directorName
  if (match.directorPosition) next.directorPosition = match.directorPosition
  return next
}

/**
 * Форма создания клиента-компании. Реквизиты опциональны (можно создать с одним
 * наименованием + логином), но при заполнении проверяются клиентом ЗЕРКАЛЬНО серверу
 * (validateRequisites): формат — только при непустом значении, КПП обязателен для юрлица
 * (ИНН=10 цифр). Серверные ошибки 422 (`details[].field`) раскладываются по инлайн-полям;
 * общий баннер остаётся для не-полевых ошибок.
 */
export function CreateClientForm({ onCreated }: { onCreated: (client: CreatedClient) => void }) {
  const [form, setForm] = useState<CreateClientInput>(EMPTY_FORM)
  const [loginMode, setLoginMode] = useState<LoginMode>('new')
  const [errors, setErrors] = useState<RequisiteErrors>({})
  const [serverError, setServerError] = useState<unknown>(null)
  const [submitting, setSubmitting] = useState(false)

  // Автозаполнение по ИНН через DaData (p2-dadata, §3.3, design D11): клиент ещё не
  // создан — teamApi.dadataLookup вызывается БЕЗ cid (design D3).
  const [dadataLoading, setDadataLoading] = useState(false)
  const [dadataError, setDadataError] = useState<string | null>(null)
  const [dadataMatches, setDadataMatches] = useState<DaDataPartyMatch[] | null>(null)

  function applyDaDataMatch(match: DaDataPartyMatch) {
    setForm((f) => applyDaDataMatchToClientForm(f, match))
    setDadataMatches(null)
    setDadataError(null)
  }

  async function lookupDaData() {
    const inn = form.inn ?? ''
    if (!isValidInn(inn)) return
    setDadataError(null)
    setDadataMatches(null)
    setDadataLoading(true)
    try {
      const matches = await teamApi.dadataLookup(inn)
      if (matches.length === 0) {
        setDadataError('Организация с таким ИНН не найдена')
      } else if (matches.length === 1) {
        applyDaDataMatch(matches[0])
      } else {
        setDadataMatches(matches)
      }
    } catch (e) {
      setDadataError(getErrorMessage(e, 'Не удалось выполнить автозаполнение по ИНН'))
    } finally {
      setDadataLoading(false)
    }
  }

  function set<K extends keyof CreateClientInput>(key: K, value: CreateClientInput[K]) {
    setForm((f) => ({ ...f, [key]: value }))
    // Правка поля снимает его инлайн-ошибку (локальную) — серверная отвалится при сабмите.
    setErrors((prev) => {
      if (!(key in prev)) return prev
      const next = { ...prev }
      delete next[key as keyof RequisiteErrors]
      return next
    })
  }

  /** Сообщение по полю: приоритет локальной валидации, затем серверный details. */
  function err(key: keyof CreateClientInput): string | undefined {
    return (errors as Record<string, string | undefined>)[key] ?? fieldError(serverError, key)
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setServerError(null)
    const local = validateRequisites({
      inn: form.inn ?? '',
      kpp: form.kpp ?? '',
      ogrn: form.ogrn ?? '',
      okpo: form.okpo ?? '',
      oktmo: form.oktmo ?? '',
      okved: form.okved ?? '',
      email: form.email ?? '',
      phone: form.phone ?? '',
      legalAddress: form.legalAddress ?? '',
    })
    setErrors(local)
    if (Object.keys(local).length > 0) return
    setSubmitting(true)
    try {
      // Уходит только выбранный вариант входа: сервер принимает ровно один.
      const { loginEmail, password, existingLoginEmail, ...company } = form
      const login =
        loginMode === 'existing'
          ? { existingLoginEmail: (existingLoginEmail ?? '').trim() }
          : { loginEmail, password }
      const { client } = await teamApi.createClient({ ...company, ...login, name: form.name.trim() })
      onCreated(client)
      setForm(EMPTY_FORM)
      setLoginMode('new')
      setErrors({})
    } catch (err2) {
      setServerError(err2)
    } finally {
      setSubmitting(false)
    }
  }

  /** Текстовое поле реквизита: подпись + значение + инлайн-ошибка (по ключу формы). */
  const text = (
    label: string,
    key: keyof CreateClientInput,
    opts: { required?: boolean; type?: string; placeholder?: string } = {},
  ) => (
    <Field label={label} required={opts.required} className="my-0" error={err(key)}>
      {(props) => (
        <Input
          {...props}
          type={opts.type}
          placeholder={opts.placeholder}
          value={(form[key] as string | undefined) ?? ''}
          onChange={(e) => set(key, e.target.value as CreateClientInput[typeof key])}
          required={opts.required}
        />
      )}
    </Field>
  )

  return (
    <Card header="Новый клиент-компания" className="mb-4">
      <form onSubmit={onSubmit} noValidate>
        {serverError ? (
          <Alert variant="danger" className="mb-4">
            {getErrorMessage(serverError, 'Не удалось создать клиента')}
          </Alert>
        ) : null}
        <Field required label="Наименование организации" error={err('name')}>
          {(props) => (
            <Input {...props} value={form.name} onChange={(e) => set('name', e.target.value)} required />
          )}
        </Field>
        <div className="mb-4">
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => void lookupDaData()}
              disabled={!isValidInn(form.inn ?? '') || dadataLoading}
            >
              {dadataLoading ? 'Ищем в DaData…' : 'Заполнить по ИНН'}
            </Button>
            {!isValidInn(form.inn ?? '') ? <Hint>Введите корректный ИНН (10 или 12 цифр)</Hint> : null}
          </div>
          {dadataError ? (
            <Alert variant="danger" className="mt-2">
              {dadataError}
            </Alert>
          ) : null}
          {dadataMatches && dadataMatches.length > 1 ? (
            <div className="mt-2 space-y-2" role="listbox" aria-label="Выберите организацию">
              {dadataMatches.map((m, i) => (
                <button
                  key={`${m.inn}-${m.kpp ?? i}`}
                  type="button"
                  role="option"
                  aria-selected={false}
                  className="w-full rounded-8 border border-line-strong p-3 text-left transition-colors hover:bg-hover"
                  onClick={() => applyDaDataMatch(m)}
                >
                  <div className="text-14 font-medium text-txt">{m.nameFull}</div>
                  <div className="text-12 text-txt-secondary">
                    ИНН {m.inn}
                    {m.legalAddress ? ` · ${m.legalAddress}` : ''}
                  </div>
                </button>
              ))}
            </div>
          ) : null}
        </div>
        <Grid cols={2}>
          {text('ИНН', 'inn')}
          {/* КПП обязателен только у юрлица (ИНН = 10 цифр) — как в `validateRequisites`. */}
          {text('КПП', 'kpp', { required: isLegalEntityInn(form.inn ?? '') })}
          {text('ОГРН', 'ogrn')}
          {text('ОКПО', 'okpo')}
          {text('ОКТМО', 'oktmo')}
          {text('ОКВЭД', 'okved', { placeholder: 'Например: 85.42.9' })}
        </Grid>
        <Field label="Юридический адрес" error={err('legalAddress')}>
          {(props) => (
            <Input
              {...props}
              value={form.legalAddress}
              onChange={(e) => set('legalAddress', e.target.value)}
            />
          )}
        </Field>
        <Grid cols={2}>
          {text('Руководитель', 'directorName')}
          {text('Должность руководителя', 'directorPosition')}
          {text('Email для документов', 'email', { type: 'email' })}
          {text('Телефон', 'phone', { type: 'tel', placeholder: '+7 999 123-45-67' })}
        </Grid>
        <div className="mt-4 border-t border-line pt-4">
          <div className="mb-2 text-13 font-medium text-txt">Вход клиента в кабинет</div>
          <div className="mb-3 flex flex-wrap gap-2" role="group" aria-label="Вход клиента в кабинет">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className={cn(loginMode === 'new' && SELECTED_MODE)}
              aria-pressed={loginMode === 'new'}
              onClick={() => setLoginMode('new')}
            >
              Новый вход
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className={cn(loginMode === 'existing' && SELECTED_MODE)}
              aria-pressed={loginMode === 'existing'}
              onClick={() => setLoginMode('existing')}
            >
              Вход специалиста, который уже работает в кабинете
            </Button>
          </div>
          {loginMode === 'new' ? (
            <>
              <p className="mb-3 text-12 text-txt-secondary">
                Общий вход на организацию (роль «компания»). Передайте клиенту — он сменит пароль в
                «Настройках».
              </p>
              <Grid cols={2}>
                <Field required label="Email для входа" className="my-0" error={err('loginEmail')}>
                  {(props) => (
                    <Input
                      {...props}
                      type="email"
                      autoComplete="off"
                      value={form.loginEmail}
                      onChange={(e) => set('loginEmail', e.target.value)}
                      required
                    />
                  )}
                </Field>
                <Field required label="Начальный пароль" className="my-0" error={err('password')}>
                  {(props) => (
                    <PasswordInput
                      {...props}
                      autoComplete="new-password"
                      value={form.password}
                      onChange={(e) => set('password', e.target.value)}
                      required
                    />
                  )}
                </Field>
              </Grid>
            </>
          ) : (
            <>
              <p className="mb-3 text-12 text-txt-secondary">
                Для специалиста, который ведёт несколько организаций. Новая организация сразу
                закрепится за его входом и появится у него в списке организаций. Отдельная почта,
                пароль и письмо с доступом не нужны — сообщите специалисту сами.
              </p>
              <Field
                required
                label="Почта входа специалиста"
                hint="Почта, по которой специалист входит в кабинет другой своей организации"
                className="my-0 max-w-md"
                error={err('existingLoginEmail')}
              >
                {(props) => (
                  <Input
                    {...props}
                    type="email"
                    autoComplete="off"
                    placeholder="specialist@company.ru"
                    value={form.existingLoginEmail}
                    onChange={(e) => set('existingLoginEmail', e.target.value)}
                    required
                  />
                )}
              </Field>
            </>
          )}
        </div>
        <Button
          type="submit"
          className="mt-4"
          disabled={
            submitting ||
            form.name.trim() === '' ||
            (loginMode === 'existing'
              ? (form.existingLoginEmail ?? '').trim() === ''
              : (form.loginEmail ?? '').trim() === '' || form.password === '')
          }
        >
          {submitting ? 'Создание…' : 'Создать клиента'}
        </Button>
      </form>
    </Card>
  )
}

/** Размер страницы списка клиентов (требование: не более 20 на странице). */
const PAGE_SIZE = 20

/** Значение фильтра «все менеджеры»: у Radix Select пустая строка зарезервирована. */
const ALL_MANAGERS = '__all__'

export function ClientsPage() {
  const navigate = useNavigate()
  const seesAll = useSeesAllClients()
  const [items, setItems] = useState<ClientSummary[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  // Фильтр по менеджеру (team-access): все / без менеджера / клиенты сотрудника.
  const [manager, setManager] = useState(ALL_MANAGERS)
  // Фильтр по менеджеру по сопровождению (client-support-manager) — так же.
  const [supportManager, setSupportManager] = useState(ALL_MANAGERS)
  // Плашка «Без менеджера по сопровождению: N» — тем, кто назначает (администратор, руководитель).
  const { context } = useAuth()
  const assigner = canAssign(context?.user.role)
  const [withoutSupport, setWithoutSupport] = useState<number | null>(null)
  const staff = useStaff()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  // Организация, созданная с закреплённым входом специалиста: письмо ему не уходит, поэтому
  // напоминаем сообщить ему самим.
  const [linkedCreated, setLinkedCreated] = useState<CreatedClient | null>(null)
  // reloadKey — принудительная перезагрузка текущего запроса (после создания клиента),
  // когда ни поиск, ни номер страницы не изменились.
  const [reloadKey, setReloadKey] = useState(0)

  // Дебаунс ввода (как в очереди заказов): поиск идёт на сервер, дёргать его на каждый
  // символ незачем. Изменение запроса открывает первую страницу результатов (требование).
  // Очистка поля (крестиком или вручную) применяется СРАЗУ: сброс поиска по требованию —
  // одно действие, ждать паузу набора там нечего.
  useEffect(() => {
    const next = search.trim()
    if (next === '') {
      setQuery('')
      setPage(1)
      return
    }
    const t = setTimeout(() => {
      setQuery(next)
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    teamApi
      .listClients(
        {
          q: query || undefined,
          manager: manager === ALL_MANAGERS ? undefined : manager,
          supportManager: supportManager === ALL_MANAGERS ? undefined : supportManager,
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
        },
        controller.signal,
      )
      .then((res) => {
        if (controller.signal.aborted) return
        // Страница «уехала» за пределы результатов (поиск сузил список) — открываем первую.
        if (res.items.length === 0 && page > 1) {
          setPage(1)
          return
        }
        setItems(res.items)
        setTotal(res.total)
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(getErrorMessage(err, 'Не удалось загрузить клиентов'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [query, manager, supportManager, page, reloadKey])

  // Счётчик клиентов без менеджера по сопровождению — при открытии и после создания клиента.
  useEffect(() => {
    if (!assigner) return
    const controller = new AbortController()
    teamApi
      .countClientsWithoutSupportManager(controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setWithoutSupport(res.count)
      })
      .catch(() => undefined)
    return () => controller.abort()
  }, [assigner, reloadKey])

  function onCreated(client: CreatedClient) {
    // Новый клиент виден, если попадает в текущий поиск: перечитываем ПЕРВУЮ страницу с
    // тем же запросом, а не вставляем строку локально (иначе она появилась бы и в чужой
    // выборке, и на не-первой странице).
    setShowForm(false)
    setLinkedCreated(client.existingLogin ? client : null)
    setPage(1)
    setReloadKey((k) => k + 1)
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div>
      <PageHead
        title="Клиенты"
        subtitle={seesAll ? 'Компании-клиенты Центрики' : 'Клиенты, закреплённые за вами'}
        actions={
          <Button
            leftIcon={<Building2 className="size-4 stroke-current" aria-hidden />}
            onClick={() => {
              setShowForm((s) => !s)
              setLinkedCreated(null)
            }}
          >
            Добавить клиента
          </Button>
        }
      />

      {showForm ? <CreateClientForm onCreated={onCreated} /> : null}
      {linkedCreated ? (
        <Alert variant="info" className="mb-4">
          Организация {linkedCreated.name} создана и закреплена за входом {linkedCreated.loginEmail}.
          Сообщите специалисту: организация появится у него в списке после входа или обновления
          страницы.
        </Alert>
      ) : null}

      {assigner && withoutSupport ? (
        <Alert variant="info" className="mb-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>Без менеджера по сопровождению: {withoutSupport}</span>
            {supportManager === 'none' ? null : (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  // Только клиенты без сопровождения — остальные фильтры не сужают список.
                  setSupportManager('none')
                  setManager(ALL_MANAGERS)
                  setPage(1)
                }}
              >
                Показать
              </Button>
            )}
          </div>
        </Alert>
      ) : null}

      {/* Ярлык панели отличается от доступного имени самого поля («Поиск клиента»),
          иначе на странице было бы два элемента с одинаковым именем. */}
      <Toolbar aria-label="Поиск клиентов">
        <ClientSearch value={search} onChange={setSearch} className="max-w-narrow flex-1" />
        <Select
          value={manager}
          onValueChange={(v) => {
            setManager(v)
            setPage(1)
          }}
        >
          <SelectTrigger className="w-56" aria-label="Менеджер">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_MANAGERS}>Все менеджеры</SelectItem>
            <SelectItem value="none">Без менеджера</SelectItem>
            {staffOptions(staff).map((o) => (
              <SelectItem key={o.id} value={o.id}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={supportManager}
          onValueChange={(v) => {
            setSupportManager(v)
            setPage(1)
          }}
        >
          <SelectTrigger className="w-64" aria-label="Менеджер по сопровождению">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_MANAGERS}>Любое сопровождение</SelectItem>
            <SelectItem value="none">Без сопровождения</SelectItem>
            {staffOptions(staff).map((o) => (
              <SelectItem key={o.id} value={o.id}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Toolbar>

      <Panel>
        {error ? (
          <Alert variant="danger" className="m-4">
            {error}
          </Alert>
        ) : loading ? (
          <p className="px-4 py-10 text-center text-txt-secondary">Загрузка…</p>
        ) : items.length === 0 ? (
          // Пустой список без поиска и «ничего не найдено» — разные состояния (требование).
          query !== '' || manager !== ALL_MANAGERS || supportManager !== ALL_MANAGERS ? (
            <EmptyState
              icon={Building2}
              title="Клиенты не найдены"
              description={
                manager === ALL_MANAGERS && supportManager === ALL_MANAGERS
                  ? 'Измените запрос поиска'
                  : 'Измените запрос поиска или фильтры'
              }
            />
          ) : (
            <EmptyState
              icon={Building2}
              title={seesAll ? 'Клиентов пока нет' : 'За вами пока не закреплён ни один клиент'}
              description={
                seesAll
                  ? 'Создайте первого клиента-компанию'
                  : 'Клиент появится здесь, когда руководитель назначит вас его менеджером или ответственным в его заказе'
              }
            />
          )
        ) : (
          <DataTable>
            <DataTableHead>
              <DataTableRow>
                <DataTableHeaderCell>Организация</DataTableHeaderCell>
                <DataTableHeaderCell>ИНН</DataTableHeaderCell>
                <DataTableHeaderCell>Менеджер</DataTableHeaderCell>
                <DataTableHeaderCell>Сопровождение</DataTableHeaderCell>
              </DataTableRow>
            </DataTableHead>
            <DataTableBody>
              {items.map((c) => (
                <DataTableRow
                  key={c.id}
                  clickable
                  onClick={() => navigate(`/team/clients/${c.id}`)}
                >
                  <DataTableCell className="font-medium">{c.name}</DataTableCell>
                  <DataTableCell className="text-txt-tertiary">{c.inn ?? '—'}</DataTableCell>
                  <DataTableCell className={c.managerName ? undefined : 'text-txt-tertiary'}>
                    {c.managerName || 'Не назначен'}
                  </DataTableCell>
                  <DataTableCell className={c.supportManagerName ? undefined : 'text-txt-tertiary'}>
                    {c.supportManagerName || 'Не назначен'}
                  </DataTableCell>
                </DataTableRow>
              ))}
            </DataTableBody>
          </DataTable>
        )}
      </Panel>
      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      <Hint className="mt-2.5">
        {seesAll
          ? 'Вы видите данные всех клиентов.'
          : 'Вы видите клиентов, у которых вы менеджер или менеджер по сопровождению, и клиентов, в заказах которых вы ответственный.'}
      </Hint>
    </div>
  )
}
